import React from 'react';
import { useAuth } from '../hooks/useAuth';
import Logo from './Logo';

export default function LandingPage({ onLoginClick, onSignupClick, onDashboardClick }) {
  const { user } = useAuth();
  const primaryLabel = user ? 'Open My Workspace' : 'Create account';
  const primaryAction = user ? onDashboardClick : onSignupClick;

  return (
    <div className="gh-landing">
      <header className="gh-landing-nav">
        <div className="gh-landing-wrap">
          <div className="gh-landing-brand">
            <div className="gh-landing-brand-mark">
              <Logo width={22} height={22} />
            </div>
            <span>GoHalfsies</span>
          </div>

          <div className="gh-landing-nav-actions">
            {!user && (
              <button type="button" className="gh-btn gh-btn-ghost" onClick={onLoginClick}>
                Log in
              </button>
            )}
            <button type="button" className="gh-btn gh-btn-solid" onClick={primaryAction}>
              {primaryLabel}
            </button>
          </div>
        </div>
      </header>

      <main className="gh-landing-main">
        <section className="gh-hero gh-landing-wrap">
          <div className="gh-hero-content">
            <div className="gh-kicker">Built for couples sharing real life</div>
            <h1>Track shared expenses without awkward math.</h1>
            <p>
              GoHalfsies keeps a clear running balance between you and your partner.
              Add expenses, settle up when you want, and keep every decision transparent.
            </p>
            <div className="gh-hero-actions">
              <button type="button" className="gh-btn gh-btn-solid gh-btn-lg" onClick={primaryAction}>
                {primaryLabel}
              </button>
              <button
                type="button"
                className="gh-btn gh-btn-ghost gh-btn-lg"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              >
                See How It Works
              </button>
            </div>
          </div>

          <div className="gh-hero-panel">
            <div className="gh-balance-card">
              <div className="gh-balance-label">Current Balance</div>
              <div className="gh-balance-value">$128.40</div>
              <p>You are owed by your partner</p>
            </div>

            <div className="gh-activity">
              <h3>Recent shared activity</h3>
              <ul>
                <li>
                  <span>Groceries</span>
                  <strong>$86.20</strong>
                </li>
                <li>
                  <span>Internet bill</span>
                  <strong>$70.00</strong>
                </li>
                <li>
                  <span>Coffee run</span>
                  <strong>$18.00</strong>
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="gh-proof gh-landing-wrap" id="how-it-works">
          <article>
            <h2>1. Create your shared space</h2>
            <p>Invite your partner with a code and start tracking in one place.</p>
          </article>
          <article>
            <h2>2. Add expenses in seconds</h2>
            <p>Record who paid and how it should be split, including custom reimbursements.</p>
          </article>
          <article>
            <h2>3. Settle with confidence</h2>
            <p>See exactly who owes what and settle up when the time feels right.</p>
          </article>
        </section>

        <section className="gh-final gh-landing-wrap">
          <h2>Clarity first. Stress last.</h2>
          <p>
            No spreadsheets, no guesswork, no hidden math. Just a shared ledger that keeps both of you aligned.
          </p>
          <button type="button" className="gh-btn gh-btn-solid gh-btn-lg" onClick={primaryAction}>
            {primaryLabel}
          </button>
        </section>
      </main>

      <style>{`
        .gh-landing {
          min-height: 100vh;
          background:
            radial-gradient(1200px 420px at 80% -10%, #dfe6ff 0%, rgba(223,230,255,0) 60%),
            var(--bg);
          color: var(--text-1);
        }

        .gh-landing-wrap {
          max-width: 1120px;
          margin: 0 auto;
          padding-left: 24px;
          padding-right: 24px;
        }

        .gh-landing-nav {
          position: sticky;
          top: 0;
          z-index: 20;
          backdrop-filter: blur(8px);
          background: rgba(245, 245, 245, 0.85);
          border-bottom: 1px solid var(--border);
        }

        .gh-landing-nav .gh-landing-wrap {
          min-height: 74px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .gh-landing-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          font-family: var(--font-display);
          font-size: 30px;
          font-weight: 600;
          line-height: 1;
          letter-spacing: 0.01em;
        }

        .gh-landing-brand-mark {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          background: rgba(17, 17, 17, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .gh-landing-nav-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .gh-btn {
          min-height: 38px;
          border-radius: var(--r-sm);
          padding: 0 14px;
          font-size: 13px;
          font-weight: 600;
          border: 1px solid transparent;
          transition: all 0.16s ease;
        }

        .gh-btn:active { transform: scale(0.98); }

        .gh-btn-solid {
          background: var(--accent);
          color: #fff;
          box-shadow: var(--shadow-sm);
        }

        .gh-btn-solid:hover { background: var(--accent-dark); }

        .gh-btn-ghost {
          background: var(--surface);
          color: var(--text-1);
          border-color: var(--border);
        }

        .gh-btn-ghost:hover {
          background: var(--surface-2);
          border-color: var(--border-2);
        }

        .gh-btn-lg {
          min-height: 44px;
          font-size: 14px;
          padding: 0 18px;
        }

        .gh-landing-main {
          padding-top: 26px;
          padding-bottom: 70px;
        }

        .gh-hero {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 430px);
          gap: 34px;
          align-items: stretch;
        }

        .gh-hero-content {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--r-xl);
          padding: 30px;
          box-shadow: var(--shadow-sm);
          animation: ghFadeUp 380ms ease both;
        }

        .gh-kicker {
          display: inline-flex;
          padding: 5px 10px;
          border-radius: 999px;
          border: 1px solid var(--border);
          background: var(--surface-2);
          color: var(--text-2);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 14px;
        }

        .gh-hero-content h1 {
          font-family: var(--font-display);
          font-size: clamp(42px, 7vw, 70px);
          line-height: 0.98;
          letter-spacing: -0.02em;
          margin: 0 0 16px;
          max-width: 14ch;
        }

        .gh-hero-content p {
          margin: 0;
          color: var(--text-2);
          font-size: 16px;
          max-width: 52ch;
        }

        .gh-hero-actions {
          display: flex;
          gap: 10px;
          margin-top: 24px;
          flex-wrap: wrap;
        }

        .gh-hero-panel {
          border-radius: var(--r-xl);
          background: linear-gradient(160deg, #111 0%, #1b1b1b 100%);
          color: #f5f5f5;
          padding: 20px;
          border: 1px solid #252525;
          box-shadow: var(--shadow-lg);
          animation: ghFadeUp 500ms ease both;
          animation-delay: 80ms;
        }

        .gh-balance-card {
          border: 1px solid rgba(255, 255, 255, 0.16);
          border-radius: var(--r-lg);
          background: rgba(255, 255, 255, 0.04);
          padding: 16px;
        }

        .gh-balance-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.09em;
          color: rgba(255, 255, 255, 0.62);
          margin-bottom: 8px;
        }

        .gh-balance-value {
          font-size: 38px;
          font-weight: 700;
          letter-spacing: -0.03em;
          line-height: 1;
          color: #7f95ff;
        }

        .gh-balance-card p {
          margin: 8px 0 0;
          color: rgba(255, 255, 255, 0.74);
          font-size: 13px;
        }

        .gh-activity {
          margin-top: 14px;
        }

        .gh-activity h3 {
          margin: 0 0 10px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: rgba(255, 255, 255, 0.65);
        }

        .gh-activity ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .gh-activity li {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: var(--r-sm);
          padding: 10px 12px;
          background: rgba(255, 255, 255, 0.03);
          font-size: 13px;
        }

        .gh-activity li span { color: rgba(255,255,255,0.8); }
        .gh-activity li strong { color: #fff; font-feature-settings: "tnum"; }

        .gh-proof {
          margin-top: 18px;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }

        .gh-proof article {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--r-lg);
          padding: 18px;
          box-shadow: var(--shadow-sm);
        }

        .gh-proof h2 {
          margin: 0 0 8px;
          font-size: 17px;
          line-height: 1.2;
          letter-spacing: -0.01em;
        }

        .gh-proof p {
          margin: 0;
          color: var(--text-2);
          font-size: 13px;
        }

        .gh-final {
          margin-top: 18px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--r-xl);
          padding: 30px;
          box-shadow: var(--shadow-sm);
          text-align: center;
        }

        .gh-final h2 {
          margin: 0;
          font-family: var(--font-display);
          font-size: clamp(34px, 6vw, 52px);
          line-height: 0.98;
          letter-spacing: -0.02em;
        }

        .gh-final p {
          margin: 12px auto 20px;
          color: var(--text-2);
          max-width: 62ch;
          font-size: 15px;
        }

        @media (max-width: 980px) {
          .gh-hero {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .gh-proof {
            grid-template-columns: 1fr;
          }

          .gh-landing-main {
            padding-top: 16px;
          }
        }

        @media (max-width: 640px) {
          .gh-landing-wrap {
            padding-left: 14px;
            padding-right: 14px;
          }

          .gh-landing-nav .gh-landing-wrap {
            min-height: 62px;
          }

          .gh-landing-brand {
            font-size: 23px;
          }

          .gh-landing-brand-mark {
            width: 30px;
            height: 30px;
          }

          .gh-hero-content,
          .gh-final {
            padding: 20px;
          }

          .gh-hero-content p,
          .gh-final p {
            font-size: 14px;
          }

          .gh-btn-lg {
            width: 100%;
          }

          .gh-hero-actions {
            gap: 8px;
          }
        }

        @keyframes ghFadeUp {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
